import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material'
import { ReactElement, useState } from 'react'
import axios from 'src/utilities/http-client'
import { DownloadIcon } from 'src/v3/core/icons'
import { downloadFile } from 'src/v3/utilities/filesystem'
import { fetchCourseVideoDownloadUrl } from 'src/v3/content-pipeline/utils/course-api'

type Props = {
  isOpen: boolean
  onClose: () => void
  videoId: string
  videoName: string
}

export default function DownloadVideoModal({
  isOpen,
  onClose,
  videoId,
  videoName,
}: Props): ReactElement {
  let [downloading, setDownloading] = useState(false)
  let [progress, setProgress] = useState(0)
  let [error, setError] = useState('')

  let handleDownload = async () => {
    setDownloading(true)
    setProgress(0)
    setError('')

    try {
      let presignedUrl = await fetchCourseVideoDownloadUrl(videoId)

      let videoResponse = await axios.get(presignedUrl, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total),
            )
          }
        },
      })

      let fileName = `${videoName.replace(/\s+/g, '_')}_${videoId}.mp4`
      downloadFile(videoResponse.data, fileName)

      onClose()
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to download video. Please try again.',
      )
      setDownloading(false)
      setProgress(0)
    }
  }

  let handleClose = () => {
    if (!downloading) {
      setError('')
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Download Video</DialogTitle>
      <DialogContent>
        {!downloading && !error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Download the source video file for:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {videoName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The video file will be downloaded to your default downloads
              folder. Large files may take some time to download.
            </Typography>
          </Box>
        )}

        {downloading && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress
                variant="determinate"
                value={progress}
                size={80}
                thickness={4}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" component="div" color="text.secondary">
                  {`${progress}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {progress < 100
                ? 'Downloading video...'
                : 'Preparing download...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few minutes for large files
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mt: 3 }}
            />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.dark',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.main',
            }}
          >
            <Typography variant="body2" color="error.light">
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={downloading}>
          {error ? 'Close' : 'Cancel'}
        </Button>
        {!error && (
          <Button
            onClick={handleDownload}
            variant="contained"
            disabled={downloading}
            startIcon={downloading ? undefined : <DownloadIcon />}
          >
            {downloading ? 'Downloading...' : 'Download Video'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
